import { ArrowLeftIcon, KeyIcon } from '@phosphor-icons/react'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { MIN_ACCOUNT_PASSWORD_LENGTH, requestEmailCode, resetPassword } from '../../api/userland'
import { AccountVaultMigrationCommittedError, useAccountVault } from '../../hooks/useAccountVault'
import { AccountVaultSecretMismatchError } from '../../lib/clientVault'
import soft from '../../styles/soft.module.css'
import { useAccountSession } from '../../wallet/AccountSessionContext'
import { useWallets } from '../../wallet/WalletsContext'
import styles from './AuthShell.module.css'
import {
  CodeField,
  ResendCode,
  emailCodeFailMessage,
  useResendCountdown,
  wellFormedCode,
} from './emailCode'

// "Forgot password": a reset code by email, a new password, then the wallet.
//
// The email account's password also wraps its wallet key (the vault's
// password slot). The code proves the address to the server, not to the
// vault: after the reset the slot still opens with the old password. When the
// key service opened the account's login secret for this reset (ADR-055 step
// 4), the slot is re-wrapped with it at once, and the login secret is then
// replaced by a new one (rotateLoginSecret), so the one the reset released
// opens nothing. The key service releases it only to an address a code
// confirmed before the reset (operator, 2026-11-05); for any other the
// recovery key does it, and Settings confirms the address. "Later" leaves the
// wallet locked, and the wallet menu's form takes the recovery key afterwards
// (ProfilePopover).

type Step = 'email' | 'code' | 'wallet' | 'notice'

export function ForgotPassword({
  initialEmail,
  onBack,
  onDone,
}: {
  initialEmail: string
  onBack: () => void
  onDone: () => void
}) {
  const session = useAccountSession()
  const { reload } = useWallets()
  const vault = useAccountVault()
  const resend = useResendCountdown()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [recoveryKey, setRecoveryKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [walletHint, setWalletHint] = useState<WalletHint>('recovery')
  // The new password, kept only until the wallet step is done or left.
  const newPassword = useRef('')
  const sentTo = useRef('')
  useEffect(
    () => () => {
      newPassword.current = ''
    },
    []
  )

  const sendCode = async () => {
    // A code for this address went out less than a minute ago: it is still
    // good, and the API would refuse a new one.
    if (sentTo.current === email.trim() && resend.left > 0) {
      setError(null)
      setStep('code')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await requestEmailCode(email.trim(), 'reset')
      sentTo.current = email.trim()
      resend.start()
      setStep('code')
    } catch (err) {
      setError(emailCodeFailMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const submitEmail = (event: FormEvent) => {
    event.preventDefault()
    if (!email.includes('@')) {
      setError('A valid email address is required')
      return
    }
    void sendCode()
  }

  const finish = async () => {
    newPassword.current = ''
    await reload()
    onDone()
  }

  const submitReset = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    if (!wellFormedCode(code)) {
      setError('Enter the 6-digit code from the email')
      return
    }
    if (password.length < MIN_ACCOUNT_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_ACCOUNT_PASSWORD_LENGTH} characters`)
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setBusy(true)
    const submitted = password
    let result: Awaited<ReturnType<typeof resetPassword>>
    try {
      result = await resetPassword(email.trim(), code.trim(), submitted)
    } catch (err) {
      setError(emailCodeFailMessage(err))
      setBusy(false)
      return
    }
    setPassword('')
    setConfirm('')
    newPassword.current = submitted
    await session.reload()
    try {
      const slot = await vault.setPasswordSlot(
        submitted,
        result.loginSecret ? { loginSecret: result.loginSecret } : undefined
      )
      if (slot === 'needs-secret') {
        setWalletHint(
          result.emailUnverified
            ? 'unconfirmed'
            : result.loginSecretUnavailable
              ? 'key-service'
              : 'recovery'
        )
        setStep('wallet')
      } else if (slot === 'legacy') {
        setNotice('Your password is changed. The wallet still opens with the old password.')
        setStep('notice')
      } else if (result.loginSecret) {
        const rotated = await vault.rotateLoginSecret(result.loginSecret).then(
          () => true,
          () => false
        )
        if (rotated) {
          await finish()
        } else {
          setNotice(
            'Your password is changed and the wallet is unlocked. Its sign-in key could not be renewed: reset the password again to renew it.'
          )
          setStep('notice')
        }
      } else {
        await finish()
      }
    } catch {
      setNotice(
        'Your password is changed, but the wallet could not be unlocked. Unlock it from the wallet menu.'
      )
      setStep('notice')
    } finally {
      result.loginSecret?.fill(0)
      setBusy(false)
    }
  }

  const submitWallet = async (event: FormEvent) => {
    event.preventDefault()
    if (!recoveryKey.trim()) {
      setError('Recovery key is required')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await vault.setPasswordSlot(newPassword.current, { recoveryKey: recoveryKey.trim() })
      setRecoveryKey('')
      await finish()
    } catch (err) {
      setError(walletFailMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.pane}>
      <div className={styles.head}>
        <span className={styles.headMark} aria-hidden="true">
          <KeyIcon size={18} weight="bold" />
        </span>
        <h2 className={styles.headTitle}>
          {step === 'wallet' || step === 'notice' ? 'Unlock wallet' : 'Reset password'}
        </h2>
      </div>
      {step === 'email' ? (
        <form className={styles.form} noValidate onSubmit={submitEmail}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="reset-email">
              Email
            </label>
            <input
              id="reset-email"
              className={styles.input}
              type="email"
              autoComplete="email"
              placeholder="Email"
              aria-label="Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={`${soft.action} ${styles.primary}`} disabled={busy}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      ) : step === 'code' ? (
        <form className={styles.form} noValidate onSubmit={submitReset}>
          <p className={styles.hint}>If {email.trim()} has an account, a code is on its way.</p>
          <CodeField id="reset-code" value={code} onChange={setCode} />
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="reset-password">
              New password
            </label>
            <input
              id="reset-password"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              placeholder="New password"
              aria-label="New password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="reset-confirm">
              Confirm password
            </label>
            <input
              id="reset-confirm"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              placeholder="Confirm password"
              aria-label="Confirm password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={`${soft.action} ${styles.primary}`} disabled={busy}>
            {busy ? 'Working…' : 'Set new password'}
          </button>
          <ResendCode left={resend.left} busy={busy} onResend={() => void sendCode()} />
        </form>
      ) : step === 'wallet' ? (
        <form className={styles.form} noValidate onSubmit={submitWallet}>
          <p className={styles.hint}>{WALLET_HINTS[walletHint]}</p>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="reset-recovery">
              Recovery key
            </label>
            <input
              id="reset-recovery"
              className={styles.input}
              type="text"
              autoComplete="off"
              placeholder="Recovery key"
              aria-label="Recovery key"
              value={recoveryKey}
              onChange={(event) => setRecoveryKey(event.target.value)}
            />
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={`${soft.action} ${styles.primary}`} disabled={busy}>
            {busy ? 'Working…' : 'Unlock wallet'}
          </button>
          <button
            type="button"
            className={`${styles.textLink} ${styles.inlineLink}`}
            onClick={() => void finish()}
            disabled={busy}
          >
            Later
          </button>
        </form>
      ) : (
        <div className={styles.form}>
          <p className={styles.hint}>{notice}</p>
          <button
            type="button"
            className={`${soft.action} ${styles.primary}`}
            onClick={() => void finish()}
          >
            Close
          </button>
        </div>
      )}
      {step === 'email' || step === 'code' ? (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.back}
            onClick={step === 'code' ? () => setStep('email') : onBack}
          >
            <ArrowLeftIcon size={14} weight="bold" aria-hidden="true" />
            Back
          </button>
        </div>
      ) : null}
    </div>
  )
}

type WalletHint = 'recovery' | 'key-service' | 'unconfirmed'

const WALLET_HINTS: Record<WalletHint, string> = {
  recovery:
    'Your password is changed. Enter the recovery key to unlock the wallet with it. Until then the wallet stays locked; the wallet menu takes the key later.',
  'key-service':
    'Your password is changed. The wallet could not be unlocked automatically right now. Enter the recovery key, or reset the password again later.',
  unconfirmed:
    'Your password is changed. This email was never confirmed, so it cannot unlock the wallet. Enter the recovery key, then confirm the email in Settings → Security.',
}

function walletFailMessage(err: unknown): string {
  if (err instanceof AccountVaultSecretMismatchError) return 'Recovery key is incorrect'
  if (err instanceof AccountVaultMigrationCommittedError) {
    return 'The wallet was updated. Log in again to use it.'
  }
  if (err instanceof Error && err.message.toLowerCase().includes('vault changed')) {
    return 'The wallet changed in another session. Reset the password again.'
  }
  return 'Could not unlock the wallet. Try again.'
}
